import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableRow,
  FilterBar,
  PageHeader,
  RowNumberCell,
  TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../components/ui";
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { ClioService, ManualTaskAssignmentReportItem } from "../../api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const DATE_PRESETS = [
  { value: "All", label: "All" },
  { value: "Today", label: "Today" },
  { value: "Week", label: "This week" },
  { value: "Month", label: "This month" },
  { value: "LastMonth", label: "Last month" },
] as const;

type DatePreset = (typeof DATE_PRESETS)[number]["value"] | "Custom";

type Props = {
  refreshKey?: number;
};

function getDatesForPreset(preset: DatePreset): { start: Dayjs | null; end: Dayjs | null } {
  const now = dayjs();
  switch (preset) {
    case "All":
      return { start: null, end: null };
    case "Today":
      return { start: now.startOf("day"), end: now };
    case "Week":
      return { start: now.startOf("week"), end: now };
    case "Month":
      return { start: now.startOf("month"), end: now };
    case "LastMonth":
      return {
        start: now.startOf("month").subtract(1, "month"),
        end: now.startOf("month").subtract(1, "day"),
      };
    default:
      return { start: null, end: null };
  }
}

export default function ManualTaskAssignmentReport({ refreshKey = 0 }: Props) {
  const [reportData, setReportData] = useState<ManualTaskAssignmentReportItem[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>("Month");
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const hasLoadedRef = useRef(false);
  const prevRefreshKeyRef = useRef(refreshKey);

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioManualTaskAssignmentReport(
      startDate ? startDate.format("YYYY-MM-DD") : undefined,
      endDate ? endDate.format("YYYY-MM-DD") : undefined
    )
      .then((data) => {
        setReportData(data);
        setHasLoaded(true);
        hasLoadedRef.current = true;
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading task assignment report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  // Refresh after a new assignment only if the report was already loaded.
  useEffect(() => {
    if (refreshKey === prevRefreshKeyRef.current) return;
    prevRefreshKeyRef.current = refreshKey;
    if (!hasLoadedRef.current) return;
    loadReport();
  }, [refreshKey, loadReport]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return reportData;
    }
    const lower = searchTerm.toLowerCase();
    return reportData.filter(
      (row) =>
        row.createdByUserName?.toLowerCase().includes(lower) ||
        row.matterName?.toLowerCase().includes(lower) ||
        row.taskName?.toLowerCase().includes(lower) ||
        row.assigneeName?.toLowerCase().includes(lower)
    );
  }, [reportData, searchTerm]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handlePresetChange = (preset: DatePreset) => {
    setSelectedPreset(preset);
    const { start, end } = getDatesForPreset(preset);
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Box>
      <PageHeader
        title="Assignment History"
        subtitle="Track tasks created manually through GLio."
      />

      <Box sx={{ mb: 2 }}>
        <FilterBar
          hideFilterButton
          hideClearButton
          leading={
            <>
              <FormControl size="small" sx={{ width: 180, bgcolor: "background.paper" }}>
                <InputLabel id="date-preset-label">Quick filter</InputLabel>
                <Select
                  labelId="date-preset-label"
                  label="Quick filter"
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value as DatePreset)}
                >
                  {DATE_PRESETS.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                  {selectedPreset === "Custom" && (
                    <MenuItem value="Custom" disabled>
                      Custom
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <DatePicker
                label="Start date"
                value={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  setSelectedPreset("Custom");
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 180, bgcolor: "background.paper" },
                  },
                }}
              />
              <DatePicker
                label="End date"
                value={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setSelectedPreset("Custom");
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 180, bgcolor: "background.paper" },
                  },
                }}
              />
              <TextField
                placeholder="Search assignments..."
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 280 },
                  flexGrow: { sm: 1 },
                  minWidth: 220,
                  bgcolor: "background.paper",
                }}
              />
            </>
          }
          trailing={
            <AppButton
              variant="contained"
              size="small"
              onClick={loadReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Load Report
            </AppButton>
          }
        />
      </Box>

      <AppTable
        columns={[
          { id: "matter", label: "Matter", sortable: false },
          { id: "task", label: "Task", sortable: false },
          { id: "created", label: "Date Generated", sortable: false },
          { id: "assignee", label: "Assigned To", sortable: false },
          { id: "createdBy", label: "Created By", sortable: false },
        ]}
        showRowNumber
        loading={loading && reportData.length === 0}
        isEmpty={!loading && paginatedData.length === 0}
        emptyTitle={
          hasLoaded
            ? "No task assignments found for the selected filters."
            : "Click Load Report to view task assignments."
        }
        pagination={{
          count: filteredData.length,
          page,
          rowsPerPage,
          rowsPerPageOptions: [...TABLE_ROWS_PER_PAGE_OPTIONS],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {paginatedData.map((row, index) => (
          <AppTableRow key={row.id}>
            <RowNumberCell value={page * rowsPerPage + index + 1} />
            <AppTableCell sx={{ maxWidth: 280 }}>{row.matterName}</AppTableCell>
            <AppTableCell sx={{ maxWidth: 220 }}>{row.taskName}</AppTableCell>
            <AppTableCell sx={{ whiteSpace: "nowrap" }}>
              {row.createdAt
                ? dayjs(row.createdAt).format("MM/DD/YYYY h:mm A")
                : "—"}
            </AppTableCell>
            <AppTableCell>{row.assigneeName}</AppTableCell>
            <AppTableCell>{row.createdByUserName}</AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>
    </Box>
  );
}
